<template>
    <b-modal
        :visible="visible"
        :title="title"
        size="lg"
        centered
        ok-only
        ok-title="닫기"
        content-class="stl-settlement-portal"
        @change="$emit('input', $event)"
        @hidden="onHidden"
    >
        <div class="settlement-page stl-household-modal">
            <div class="stl-seg stl-household-modal-tabs">
                <button type="button" :class="{ on: tab === 'compare' }" @click="tab = 'compare'">전월·전년 비교</button>
                <button type="button" :class="{ on: tab === 'daily' }" @click="tab = 'daily'">일별 사용량</button>
            </div>
            <div v-if="tab === 'compare'">
                <div v-if="loadingCompare" class="stl-loading"><b-spinner small variant="secondary" /><span>비교 조회 중...</span></div>
                <HouseholdCompareSnippet v-else :compare="compare" />
            </div>
            <div v-else>
                <div v-if="loadingDaily" class="stl-loading"><b-spinner small variant="secondary" /><span>일별 사용량 조회 중...</span></div>
                <DailyUsageChart
                    v-else-if="dailyChart.labels.length"
                    :key="dailyChartKey"
                    :labels="dailyChart.labels"
                    :data="dailyChart.data"
                    :unit="chartUnit"
                    :color="dailyChartColor"
                />
                <div v-else class="stl-empty"><p>일별 사용량 데이터가 없습니다.</p></div>
            </div>
        </div>
    </b-modal>
</template>

<script>
import backEndApi from "@api/backEndApi.js";
import HouseholdCompareSnippet from "./HouseholdCompareSnippet.vue";
import DailyUsageChart from "./charts/DailyUsageChart.vue";
import { dailyUsageToChart } from "../utils/chartAdapters.js";
import { parseApiError } from "../utils/settlementFormat.js";
import { energyChartColor } from "../utils/chartTheme.js";

export default {
    name: "SettlementHouseholdModal",
    components: { HouseholdCompareSnippet, DailyUsageChart },
    props: {
        value: { type: Boolean, default: false },
        site: { type: Object, required: true },
        regYm: { type: String, required: true },
        energySource: { type: String, default: "Elec" },
        sysNodeIdx: { type: [Number, String], default: null },
        roomName: { type: String, default: "" },
        billingStartAt: { type: String, default: null },
        billingEndAt: { type: String, default: null },
    },
    data() {
        return {
            tab: "compare",
            compare: null,
            loadingCompare: false,
            loadingDaily: false,
            dailyChart: { labels: [], data: [] },
            dailyChartKey: 0,
            ptMap: null,
        };
    },
    computed: {
        visible: {
            get() {
                return this.value;
            },
            set(v) {
                this.$emit("input", v);
            },
        },
        title() {
            const ym = String(this.regYm);
            const label = ym.length >= 6 ? `${ym.slice(0, 4)}-${ym.slice(4, 6)}` : ym;
            const energy =
                this.energySource === "Gas"
                    ? "가스"
                    : this.energySource === "Water"
                      ? "수도"
                      : this.energySource === "ALL"
                        ? ""
                        : "전기";
            const suffix = energy ? ` · ${energy}` : "";
            return `${this.roomName || "세대"} · ${label}${suffix}`;
        },
        chartUnit() {
            if (this.energySource === "Gas") return "Nm³";
            if (this.energySource === "Water") return "m³";
            return "kWh";
        },
        dailyChartColor() {
            return energyChartColor(this.energySource);
        },
    },
    watch: {
        value(open) {
            if (open && this.sysNodeIdx) this.onOpen();
        },
        sysNodeIdx() {
            if (this.value) this.onOpen();
        },
        energySource() {
            if (this.value) this.onOpen();
        },
        regYm() {
            if (this.value) this.onOpen();
        },
    },
    methods: {
        async onOpen() {
            this.tab = "compare";
            await Promise.all([this.loadCompare(), this.loadDaily()]);
        },
        onHidden() {
            this.compare = null;
            this.dailyChart = { labels: [], data: [] };
            this.tab = "compare";
        },
        async loadCompare() {
            if (!this.sysNodeIdx) return;
            this.loadingCompare = true;
            try {
                const { data } = await backEndApi.settlement.getCompare({
                    siteIdx: this.site.siteIdx,
                    regYm: this.regYm,
                    energySource: this.energySource,
                    sysNodeIdx: this.sysNodeIdx,
                });
                this.compare = data;
            } catch (err) {
                console.warn("[SettlementHouseholdModal]", parseApiError(err).message);
                this.compare = null;
            } finally {
                this.loadingCompare = false;
            }
        },
        async ensurePtMap() {
            if (this.ptMap) return;
            try {
                const { data } = await backEndApi.settlement.getHouseholds(this.site.siteIdx);
                const map = {};
                for (const h of data || []) {
                    map[h.sysNodeIdx] = {
                        electricPtIdx: h.electricPtIdx,
                        gasPtIdx: h.gasPtIdx,
                        waterPtIdx: h.waterPtIdx,
                    };
                }
                this.ptMap = map;
            } catch (err) {
                this.ptMap = {};
            }
        },
        async loadDaily() {
            await this.ensurePtMap();
            const ptInfo = this.ptMap?.[this.sysNodeIdx];
            let ptIdx = ptInfo?.electricPtIdx;
            if (this.energySource === "Gas") ptIdx = ptInfo?.gasPtIdx;
            if (this.energySource === "Water") ptIdx = ptInfo?.waterPtIdx;

            if (!ptIdx || !this.billingStartAt || !this.billingEndAt) {
                this.dailyChart = { labels: [], data: [] };
                return;
            }
            this.loadingDaily = true;
            try {
                const sDt = String(this.billingStartAt).slice(0, 8);
                const eDt = String(this.billingEndAt).slice(0, 8);
                const { data } = await backEndApi.summary.searchSummaryByTimeUnit(ptIdx, 3, sDt, eDt);
                this.dailyChart = dailyUsageToChart(Array.isArray(data) ? data : []);
                this.dailyChartKey += 1;
            } catch (err) {
                this.dailyChart = { labels: [], data: [] };
            } finally {
                this.loadingDaily = false;
            }
        },
    },
};
</script>
